/* ─────────────────────────────────────────────────────────────────
   MLW Quiz Inputs — Likert + Choice Renderers
   ─────────────────────────────────────────────────────────────────
   Two brand-agnostic input primitives, picked per-question by
   question.type:

     • "likert"  → N-option horizontal/grid rating buttons (TMT: 5-pt)
                   Stores integer value. Auto-advances on select.
     • "choice"  → Vertical stack of option buttons (MLW: yes/no etc.)
                   Stores string value, or { label, segment } for
                   routing questions. No auto-advance by default.

   Both renderers receive:
     • container — DOM node to mount into
     • question  — { id, type, statement, options[], routing?, ... }
     • onSelect(value) — callback fired when the user picks an option

   All styling is inline with CSS class hooks so Phase 2 brand
   themes can override via their own stylesheet.
   ───────────────────────────────────────────────────────────────── */

(function (window) {
  "use strict";

  /* ─── Small DOM helper ────────────────────────────────────── */
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var key in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, key)) continue;
        if (key === "class") node.className = attrs[key];
        else if (key === "dataset") {
          for (var dk in attrs[key]) {
            if (Object.prototype.hasOwnProperty.call(attrs[key], dk)) {
              node.dataset[dk] = attrs[key][dk];
            }
          }
        }
        else if (key in node) node[key] = attrs[key];
        else node.setAttribute(key, attrs[key]);
      }
    }
    if (children) {
      for (var i = 0; i < children.length; i++) {
        var c = children[i];
        if (c == null) continue;
        if (typeof c === "string") node.appendChild(document.createTextNode(c));
        else node.appendChild(c);
      }
    }
    return node;
  }

  function clear(container) {
    while (container.firstChild) container.removeChild(container.firstChild);
  }

  /* ─── Likert renderer ─────────────────────────────────────────
     Horizontal/grid layout. Each button displays a numeric value
     and text label. Selected state uses --quiz-primary; hover
     uses --quiz-accent. Minimum tap target 56×56px enforced via
     minHeight style. Stores integer values (1..N). */
  function renderLikert(container, question, onSelect, opts) {
    opts = opts || {};
    var currentValue = opts.currentValue;
    clear(container);

    var wrap = el("div", { class: "quiz-input quiz-input--likert" });

    var optionCount = (question.options || []).length;
    var grid = el("div", {
      class: "quiz-likert-grid",
      style: "display:grid;grid-template-columns:repeat(" + optionCount +
             ",minmax(0,1fr));gap:clamp(6px,1.5vw,12px);width:100%;max-width:600px;margin:0 auto;",
    });

    (question.options || []).forEach(function (opt) {
      var intValue = typeof opt.value === "number"
        ? opt.value
        : parseInt(opt.value, 10);
      var isSelected = currentValue === intValue;

      var btn = el("button", {
        type: "button",
        class: "quiz-likert-btn" + (isSelected ? " is-selected" : ""),
        "aria-pressed": isSelected ? "true" : "false",
        dataset: { value: String(intValue) },
        style:
          "display:flex;flex-direction:column;align-items:center;justify-content:center;" +
          "padding:clamp(14px,2.5vw,20px) 6px;min-width:56px;min-height:88px;" +
          "background:" + (isSelected ? "var(--quiz-primary)" : "var(--quiz-card-bg)") + ";" +
          "color:" + (isSelected ? "#fff" : "var(--quiz-text-dark)") + ";" +
          "border:2px solid " + (isSelected ? "var(--quiz-primary)" : "var(--quiz-progress-bg)") + ";" +
          "border-radius:var(--quiz-radius-md);cursor:pointer;" +
          "font-family:var(--quiz-font-body);" +
          "transition:transform var(--quiz-transition-speed) var(--quiz-transition-ease)," +
                     "background var(--quiz-transition-speed) var(--quiz-transition-ease)," +
                     "border-color var(--quiz-transition-speed) var(--quiz-transition-ease);",
      });

      var valueSpan = el("span", {
        class: "quiz-likert-value",
        style:
          "font-family:var(--quiz-font-heading);font-size:clamp(22px,3.5vw,30px);" +
          "font-weight:500;line-height:1;margin-bottom:6px;",
      }, [String(opt.value)]);

      var labelSpan = el("span", {
        class: "quiz-likert-label",
        style:
          "font-size:clamp(10px,1.3vw,12px);font-weight:500;line-height:1.2;" +
          "color:" + (isSelected ? "#fff" : "var(--quiz-text-med)") + ";",
      }, [opt.label || ""]);

      btn.appendChild(valueSpan);
      btn.appendChild(labelSpan);

      btn.addEventListener("mouseenter", function () {
        if (!btn.classList.contains("is-selected")) {
          btn.style.borderColor = "var(--quiz-primary)";
          btn.style.transform = "translateY(-2px)";
        }
      });
      btn.addEventListener("mouseleave", function () {
        if (!btn.classList.contains("is-selected")) {
          btn.style.borderColor = "var(--quiz-progress-bg)";
          btn.style.transform = "translateY(0)";
        }
      });

      btn.addEventListener("click", function () {
        if (typeof onSelect === "function") onSelect(intValue);
      });

      grid.appendChild(btn);
    });

    wrap.appendChild(grid);
    container.appendChild(wrap);
  }

  /* ─── Choice renderer ─────────────────────────────────────────
     Vertical button stack. Each option left-aligned with text
     label. If the question is flagged routing (e.g. MLW's
     segmenter), the stored value is an object { label, segment }
     so downstream showWhen rules can match either. */
  function renderChoice(container, question, onSelect, opts) {
    opts = opts || {};
    var currentValue = opts.currentValue;
    clear(container);

    var wrap = el("div", {
      class: "quiz-input quiz-input--choice",
      style: "display:grid;gap:10px;width:100%;max-width:480px;margin:0 auto;",
    });

    (question.options || []).forEach(function (opt) {
      var stored = question.routing
        ? { label: opt.label, value: opt.value, segment: opt.segment || null }
        : (opt.value != null ? opt.value : opt.label);

      var storedCompare = question.routing
        ? (currentValue && (currentValue.value === opt.value || currentValue.label === opt.label))
        : (currentValue === stored);

      var isSelected = !!storedCompare;

      var btn = el("button", {
        type: "button",
        class: "quiz-choice-btn" + (isSelected ? " is-selected" : ""),
        "aria-pressed": isSelected ? "true" : "false",
        style:
          "padding:18px 22px;min-height:56px;width:100%;text-align:left;cursor:pointer;" +
          "background:" + (isSelected ? "var(--quiz-progress-bg)" : "var(--quiz-card-bg)") + ";" +
          "color:var(--quiz-text-dark);" +
          "border:1.5px solid " + (isSelected ? "var(--quiz-primary)" : "var(--quiz-progress-bg)") + ";" +
          "border-radius:var(--quiz-radius-md);" +
          "font-family:var(--quiz-font-body);font-size:15px;font-weight:500;" +
          "transition:background var(--quiz-transition-speed) var(--quiz-transition-ease)," +
                     "border-color var(--quiz-transition-speed) var(--quiz-transition-ease);",
      }, [opt.label || String(opt.value)]);

      btn.addEventListener("mouseenter", function () {
        if (!btn.classList.contains("is-selected")) {
          btn.style.borderColor = "var(--quiz-primary)";
        }
      });
      btn.addEventListener("mouseleave", function () {
        if (!btn.classList.contains("is-selected")) {
          btn.style.borderColor = "var(--quiz-progress-bg)";
        }
      });

      btn.addEventListener("click", function () {
        if (typeof onSelect === "function") onSelect(stored);
      });

      wrap.appendChild(btn);
    });

    container.appendChild(wrap);
  }

  /* ─── Dispatch by question type ───────────────────────────── */
  function renderQuestion(container, question, onSelect, opts) {
    if (!question || !container) return;
    if (question.type === "likert") return renderLikert(container, question, onSelect, opts);
    /* Default to "choice" — also safe for unknown types. */
    return renderChoice(container, question, onSelect, opts);
  }

  window.MLWQuizInputs = {
    renderLikert: renderLikert,
    renderChoice: renderChoice,
    renderQuestion: renderQuestion,
  };
})(window);
